import React, { useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import {
  DataGrid, GridRowParams, GridSortDirection, GridToolbarColumnsButton, GridToolbarContainer,
  GridToolbarExport, GridToolbarFilterButton, GridValueFormatterParams
} from '@mui/x-data-grid';

import packageInfo from '../../../package.json';
import GlobalStyles from '../../global-styles';
import { formatDateForDataGrid, formatDateTimeForDataGrid } from '../../utils/helper';
import { ErrorIcon } from '../../utils/icons';
import { Box, Tooltip } from '../../utils/material-ui-core';
import Wait from '../wait';

interface ISortModel {
  field: string;
  sort: GridSortDirection;
}

interface ICustomDataGridAttribute {
  defaultColumns: any;
  defaultPageSize: number;
  defaultSortModel: any;
  defaultFilterModel: any;
  defaultPageNumber: number;
}

interface ICustomDataGridProps {
  uniqueGridCode: string;
  columns: any;
  rows: any[];
  sortModel: ISortModel[];
  showToolBar: boolean;
  handleDataGridClick: (params: GridRowParams, event: any) => void;
  handleSelectedRows: (values: any[]) => void;
}

const CustomDataGridWithSelection = ({ uniqueGridCode, columns, rows, sortModel, showToolBar, handleDataGridClick, handleSelectedRows }: ICustomDataGridProps) => {
  const pageStyles = useStyles();
  const globalStyles = GlobalStyles();

  uniqueGridCode = (uniqueGridCode + "-" + packageInfo.version.replace(".", "")).replace(".", "");

  const [customDataGridAttribute, setCustomDataGridAttribute] = useState<ICustomDataGridAttribute>({
    defaultColumns: columns,
    defaultPageSize: 25,
    defaultSortModel: sortModel,
    defaultFilterModel: {
      items: [],
    },
    defaultPageNumber: 0,
  });

  const [gridLoading, setGridLoading] = useState(true);

  const setSortModel = (model: any) => {
    if (gridLoading) {
      return;
    }
    saveCustomDataGridAttribute({
      ...customDataGridAttribute,
      defaultSortModel: model,
    });
  };

  const setFilterModel = (model: any) => {
    if (gridLoading) {
      return;
    }
    saveCustomDataGridAttribute({
      ...customDataGridAttribute,
      defaultFilterModel: model,
    });
  };

  // const handlePageSize = (newPageSize: number) => {
  //   if (gridLoading) {
  //     return;
  //   }
  //   saveCustomDataGridAttribute({
  //     ...customDataGridAttribute,
  //     defaultPageSize: newPageSize,
  //   });
  // };

  // const handlePageChange = (newPageNumber) => {
  //   saveCustomDataGridAttribute({
  //     ...customDataGridAttribute,
  //     defaultPageNumber: newPageNumber,
  //   });
  // };

  const updateTemplateColumns = (defaultColumns: any) => {
    defaultColumns.forEach((defaultColumn) => {
      if (defaultColumn.type === "datetime" || defaultColumn.type === "dateTime") {
        defaultColumn.valueFormatter = (params: GridValueFormatterParams) => formatDateTimeForDataGrid(params.value as string);
      } else if (defaultColumn.type === "date") {
        defaultColumn.valueFormatter = (params: GridValueFormatterParams) => formatDateForDataGrid(params.value as string);
      }

      if (defaultColumn.headerName.toLowerCase() === "error") {
        defaultColumn.renderCell = (params: any) => (
          <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{params.value}</div>} classes={{ tooltip: pageStyles.customWidth }} arrow>
            {params.value !== "" ? <ErrorIcon color="error" /> : <></>}
          </Tooltip>
        );
      } else if (defaultColumn.headerName.toLowerCase().indexOf(" message") >= 0) {
        defaultColumn.renderCell = (params: any) => (
          <Tooltip title={params.value} arrow>
            <span>{params.value}</span>
          </Tooltip>
        );
      }
    });
    return defaultColumns;
  };

  const handleColumnVisibilityChange = (column: any) => {
    const tempDefaultColumns = customDataGridAttribute.defaultColumns;

    tempDefaultColumns.map((defaultColumn) => {
      if (defaultColumn.field === column.field) {
        defaultColumn.hide = false;
        if (!column.isVisible) {
          defaultColumn.hide = true;
        }
      }
      return defaultColumn;
    });

    saveCustomDataGridAttribute({
      ...customDataGridAttribute,
      defaultColumns: tempDefaultColumns,
    });
  };

  const handleDataGridRowClick = (params: GridRowParams, event: any) => {
    handleDataGridClick(params, event);
  };

  const customDataGridToolbar = () => {
    return (
      <GridToolbarContainer>
        {showToolBar ? <GridToolbarColumnsButton /> : null}
        {showToolBar ? <GridToolbarFilterButton /> : null}
        {showToolBar ? <GridToolbarExport /> : null}
      </GridToolbarContainer>
    );
  };

  const saveCustomDataGridAttribute = (currentCustomDataGridAttribute: ICustomDataGridAttribute) => {
    const tempDefaultColumns = updateTemplateColumns(currentCustomDataGridAttribute.defaultColumns);
    setCustomDataGridAttribute({
      ...currentCustomDataGridAttribute,
      defaultColumns: tempDefaultColumns,
    });
    localStorage.setItem(getUniqueGridCode(), JSON.stringify(currentCustomDataGridAttribute));
  };

  const getUniqueGridCode = () => {
    return "Gridv1_" + uniqueGridCode;
  };

  useEffect(() => {
    const initializeGrid = () => {
      if (gridLoading) {
        try {
          const localStorageCustomDataGridAttribute = localStorage.getItem(getUniqueGridCode());

          if (localStorageCustomDataGridAttribute !== undefined && localStorageCustomDataGridAttribute !== null && localStorageCustomDataGridAttribute !== "") {
            saveCustomDataGridAttribute(JSON.parse(localStorageCustomDataGridAttribute) as ICustomDataGridAttribute);
          }
        } catch (e) { }
        setTimeout(() => {
          try {
            setGridLoading(false);
          }
          catch { }
        }, 100);
      }
    };

    initializeGrid();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {gridLoading ? (
        <Wait />
      ) : (
        <Box p={0} className={globalStyles.datagridRoot}>
          <DataGrid
            checkboxSelection
            rows={rows}
            columns={customDataGridAttribute.defaultColumns}
            hideFooter={true}
            filterModel={customDataGridAttribute.defaultFilterModel}
            onFilterModelChange={(model) => setFilterModel(model)}
            sortModel={customDataGridAttribute.defaultSortModel}
            onSortModelChange={(model) => setSortModel(model)}
            onColumnVisibilityChange={(column) => handleColumnVisibilityChange(column)}
            density={"standard"}
            components={{ Toolbar: customDataGridToolbar }}
            onRowClick={handleDataGridRowClick}
            disableSelectionOnClick
            disableColumnFilter
            onSelectionModelChange={(ids) => {
              const selectedIDs = new Set(ids);
              const selectedRows = rows.filter(row => selectedIDs.has(row.id));
              handleSelectedRows(selectedRows);
            }}
          />
        </Box>
      )}
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  customWidth: {
    maxWidth: 600,
    fontSize: "1.3em !important",
  },
}));

export default CustomDataGridWithSelection;
